import { typographyTokens } from '../../../../theme/typographyTokens';
import React, { useState, useMemo, useCallback } from "react";
import { Add, Email } from "@mui/icons-material";
import {
  Box,
  Button,
  Paper,
  Avatar,
  useTheme,
  Divider,
  Fade,
  Grid,
  Typography,
  Stack,
} from "@mui/material";
import EmailNotificationDialog from "./EmailNotificationDialog";
import ModalWidget from "../../../../components/ModalWidget/ModalWidget";
import {
  useUpdateEmailNotificationMutation,
  useCreateEmailNotificationMutation,
  useGetEmailNotificationsQuery,
  useDeleteEmailNotificationMutation,
} from "../../../../services/emailNotificationsSlice/emailNotificationsSlice";
import NotificationCard from "./NotificationCard";

type RecipientDetail = {
  id: string;
  displayName: string;
};

type EmailNotification = {
  id: string;
  notificationType: string;
  subject: string;
  notificationNotes: string;
  body: string;
  toRecipients: string;
  ccRecipients: string;
  toRecipientDetails?: RecipientDetail[];
  ccRecipientDetails?: RecipientDetail[];
  addRequestorOfSEP: boolean;
  addDepartmentStakeholder: boolean;
};

const splitEmails = (emails: string): string[] =>
  emails
    .split(",")
    .map((e) => e.trim())
    .filter(Boolean);

const initialTemplate: EmailNotification = {
  id: "",
  notificationType: "",
  subject: "",
  notificationNotes: "",
  body: "",
  toRecipients: "",
  ccRecipients: "",
  toRecipientDetails: [],
  ccRecipientDetails: [],
  addRequestorOfSEP: false,
  addDepartmentStakeholder: false,
};

const EmailNotifications: React.FC = () => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [emailTemplate, setEmailTemplate] =
    useState<EmailNotification>(initialTemplate);
  const [showUser, setShowUser] = useState(false);
  const [showRecipients, setShowRecipients] = useState(false);
  const [multiSelectedToUsers, setMultiSelectedToUsers] = useState<{ id: string, displayName: string }[]>([]);
  const [multiSelectedCcUsers, setMultiSelectedCcUsers] = useState<{ id: string, displayName: string }[]>([]);

  const [updateEmailNotification] = useUpdateEmailNotificationMutation();
  const [createEmailNotification] = useCreateEmailNotificationMutation();
  const { data: getEmailNotification, refetch } =
    useGetEmailNotificationsQuery();
  const [deleteEmailNotification] = useDeleteEmailNotificationMutation();

  const notifications: EmailNotification[] = useMemo(
    () =>
      Array.isArray(getEmailNotification)
        ? getEmailNotification.map((n) => ({
            id: n.id !== undefined ? String(n.id) : "",
            subject: n.subject ?? "",
            notificationNotes: n.notificationNotes ?? "",
            body: n.body ?? "",
            toRecipients: n.toRecipients ?? "",
            ccRecipients: n.ccRecipients ?? "",
            notificationType: n.notificationType ?? "",
            toRecipientDetails: n.toRecipientDetails ?? [],
            ccRecipientDetails: n.ccRecipientDetails ?? [],
            addRequestorOfSEP: n.addRequestorOfSEP ?? false,
            addDepartmentStakeholder: n.addDepartmentStakeholder ?? false,
          }))
        : [],
    [getEmailNotification]
  );

  const handleOpen = async (notification?: EmailNotification) => {
    if (notification) {
      setEmailTemplate({
        ...notification,
        toRecipientDetails: notification.toRecipientDetails ?? [],
        ccRecipientDetails: notification.ccRecipientDetails ?? [],
      });
      setShowUser(true);
    } else {
      setMultiSelectedToUsers([]);
      setMultiSelectedCcUsers([]);
      setShowUser(false);
      setShowRecipients(true);
      setEmailTemplate(initialTemplate);
    }
    setOpen(true);
  };
  const getSelectedToRecipientDetails = (id: string) => {
    const notification = notifications.find((n: any) => n.id === id);
    return notification?.toRecipientDetails || [];
  };

  const getSelectedCcRecipientDetails = (id: string) => {
    const notification = notifications.find((n: any) => n.id === id);
    return notification?.ccRecipientDetails || [];
  };

  const handleClose = useCallback(() => {
    setOpen(false);
    setEmailTemplate(initialTemplate);
  }, []);

  const handleSave = async () => {
    const trimmedTo = splitEmails(emailTemplate.toRecipients).join(", ");
    const trimmedCc = splitEmails(emailTemplate.ccRecipients).join(", ");

    if (emailTemplate.id) {
      await updateEmailNotification({
        id: parseInt(emailTemplate.id),
        body: {
          subject: emailTemplate.subject,
          body: emailTemplate.body,
          toRecipients: emailTemplate.toRecipients,
          ccRecipients: emailTemplate.ccRecipients,
          notificationType: emailTemplate.notificationType,
          notificationNotes: emailTemplate.notificationNotes,
          addRequestorOfSEP: emailTemplate.addRequestorOfSEP,
          addDepartmentStakeholder: emailTemplate.addDepartmentStakeholder,
        },
      });
    } else {
      await createEmailNotification({
        body: {
          subject: emailTemplate.subject,
          notificationNotes: emailTemplate.notificationNotes,
          body: emailTemplate.body,
          toRecipients: emailTemplate.toRecipients,
          ccRecipients: emailTemplate.ccRecipients,
          notificationType: emailTemplate.notificationType,
          addRequestorOfSEP: emailTemplate.addRequestorOfSEP,
          addDepartmentStakeholder: emailTemplate.addDepartmentStakeholder,
        },
      });
    }
    refetch();
    handleClose();
  };

  const handleDeleteNotification = useCallback(
    async (id: string) => {
      await deleteEmailNotification({ id: parseInt(id) });
      refetch();
    },
    [deleteEmailNotification, refetch]
  );

  return (
    <Fade in>
      <Box sx={{ p: { xs: 1, sm: 3 } }}>
        <Paper
          elevation={6}
          sx={{
            mx: "auto",
            p: 4,
            borderRadius: 4,
            background: theme.palette.background.paper,
            boxShadow: `0 8px 32px 0 ${theme.palette.primary.main}22`,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2} mb={3}>
            <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
              <Email />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={typographyTokens.fontWeights.bold}>
                Email Notifications
              </Typography>
              <Typography color="text.secondary">
                Manage and customize your notification emails
              </Typography>
            </Box>
            <Box flex={1} />
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpen()}
              sx={{
                borderRadius: 3,
                fontWeight: typographyTokens.fontWeights.semibold,
                color: "#fff",
                backgroundColor: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
                mx: 1,
                px: 3,
                py: 1,
                boxShadow: 2,
              }}
            >
              Add Notification
            </Button>
          </Stack>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            {notifications.map((n) => (
              <Grid item xs={12} sm={6} key={n.id}>
                <NotificationCard
                  notification={n}
                  onEdit={() => handleOpen(n)}
                  onDelete={handleDeleteNotification}
                  theme={theme}
                />
              </Grid>
            ))}
          </Grid>
        </Paper>
        <ModalWidget
          isOpen={open}
          onClose={handleClose}
          title={emailTemplate?.id ? "Edit Notification" : "Add Notification"}          
        >
          <EmailNotificationDialog
            editing={
              emailTemplate.id
                ? {
                    ...emailTemplate,
                    toRecipients: splitEmails(emailTemplate.toRecipients),
                    ccRecipients: splitEmails(emailTemplate.ccRecipients),
                  }
                : null
            }
            subject={emailTemplate.subject}
            notificationNotes={emailTemplate.notificationNotes}
            notificationType={emailTemplate.notificationType}
            body={emailTemplate.body}
            toRecipients={
              emailTemplate.id
                ? getSelectedToRecipientDetails(emailTemplate.id)
                : []
            }
            ccRecipients={
              emailTemplate.id
                ? getSelectedCcRecipientDetails(emailTemplate.id)
                : []
            }
            usedNotificationTypes={notifications
              .filter((n) => n.id !== emailTemplate.id)
              .map((n) => n.notificationType)}
            setSubject={(subject) =>
              setEmailTemplate((prev) => ({ ...prev, subject }))
            }
            setNotificationNotes={(notificationNotes) =>
              setEmailTemplate((prev) => ({ ...prev, notificationNotes }))
            }
            setNotificationType={(notificationType) =>
              setEmailTemplate((prev) => ({ ...prev, notificationType }))
            }
            setToRecipients={(toRecipients) =>
              setEmailTemplate((prev) => ({ ...prev, toRecipients }))
            }
            multiSelectedToUsers={multiSelectedToUsers}
            setMultiSelectedToUsers={setMultiSelectedToUsers}
            setCcRecipients={(ccRecipients) =>
              setEmailTemplate((prev) => ({ ...prev, ccRecipients }))
            }
            multiSelectedCcUsers={multiSelectedCcUsers}
            setMultiSelectedCcUsers={setMultiSelectedCcUsers}
            setBody={(body) => setEmailTemplate((prev) => ({ ...prev, body }))}
            handleClose={handleClose}
            handleSave={handleSave}
            showUser={showUser}
            showRecipients={showRecipients}
            setShowUser={setShowUser}
            setEmailTemplate={setEmailTemplate}
            addRequestorOfSEP={emailTemplate.addRequestorOfSEP}
            setAddRequestorOfSEP={(addRequestorOfSEP) =>
              setEmailTemplate((prev) => ({ ...prev, addRequestorOfSEP }))
            }
            addDepartmentStakeholder={emailTemplate.addDepartmentStakeholder}
            setAddDepartmentStakeholder={(addDepartmentStakeholder) =>
              setEmailTemplate((prev) => ({ ...prev, addDepartmentStakeholder }))
            }
          />
        </ModalWidget>
      </Box>
    </Fade>
  );
};

export default EmailNotifications;
